import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { db, auth } from '../../../firebase/config';
import { collection, query, where, getDocs, orderBy, limit, updateDoc, doc } from 'firebase/firestore';
import { setDocWithCreatedBy } from '../../../firebase/utils';
import { getTimestamp } from 'common/utils';
import { CashRegisterState, CashMovement, CashMovementType } from 'common/types';
import styles from './cashRegister.module.css';
import { appContext } from 'common/contexts/appContext';

type CashMovementProps = {
  isOpen: boolean;
  onClose: () => void;
  type: CashMovementType.ADDITION | CashMovementType.WITHDRAWAL;
};

const CashMovementDialog: React.FC<CashMovementProps> = ({ isOpen, onClose, type }) => {
  const [t] = useTranslation();
  const [amount, setAmount] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeRegister, setActiveRegister] = useState<CashRegisterState | null>(null);
  const [recentMovements, setRecentMovements] = useState<CashMovement[]>([]);
  const { state } = useContext(appContext);
  const isAddition = type === CashMovementType.ADDITION;

  useEffect(() => {
    if (isOpen) {
      fetchActiveRegister();
      fetchRecentMovements();
    } else {
      // Reset form when dialog closes
      setAmount('');
      setNotes('');
      setError(null);
    }
  }, [isOpen]);

  const fetchActiveRegister = async () => {
    setLoading(true);
    setError(null);
    try {
      const cashRegisterCollection = collection(db, 'cashRegister');
      const q = query(
        cashRegisterCollection,
        where('parentUserId', '==', state.parentId),
        where('isOpen', '==', true),
        orderBy('openDate', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError(t('terminal.cashRegister.noActiveRegister', 'No hay una caja abierta'));
        setActiveRegister(null);
      } else {
        const registerData = querySnapshot.docs[0].data() as CashRegisterState;
        registerData.id = querySnapshot.docs[0].id;
        setActiveRegister(registerData);
      }
    } catch (err) {
      console.error('Error fetching active register:', err);
      setError(t('terminal.cashRegister.errorFetching', 'Error al obtener el estado de la caja'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentMovements = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startOfDay = today.getTime();

      const cashMovementsCollection = collection(db, 'cashMovements');
      const q = query(
        cashMovementsCollection,
        where('parentUserId', '==', state.parentId),
        where('date', '>=', startOfDay),
        where('type', '==', type),
        orderBy('date', 'desc'),
        limit(5)
      );

      const querySnapshot = await getDocs(q);
      const movements: CashMovement[] = [];

      querySnapshot.forEach((doc) => {
        movements.push({ id: doc.id, ...doc.data() } as CashMovement);
      });

      setRecentMovements(movements);
    } catch (err) {
      console.error('Error fetching recent movements:', err);
    }
  };

  const handleSubmit = async () => {
    if (!amount.trim()) {
      setError(t('terminal.cashRegister.amountRequired', 'El monto es requerido'));
      return;
    }

    if (!activeRegister) {
      setError(t('terminal.cashRegister.noActiveRegister', 'No hay una caja abierta'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount) || numAmount <= 0) {
        throw new Error(t('terminal.cashRegister.invalidAmount', 'Monto inválido'));
      }

      // For withdrawals, check if there's enough cash
      if (type === CashMovementType.WITHDRAWAL && numAmount > activeRegister.currentAmount) {
        throw new Error(t('terminal.cashRegister.insufficientFunds', 'No hay suficiente efectivo en caja'));
      }

      // Calculate new amount
      const newAmount = type === CashMovementType.ADDITION
        ? activeRegister.currentAmount + numAmount
        : activeRegister.currentAmount - numAmount;

      const optimistFunction = async () => {
        // Update cash register
        const registerRef = doc(db, 'cashRegister', activeRegister.id);
        await updateDoc(registerRef, {
          currentAmount: newAmount,
        });

        // Create cash movement record
        const cashMovementsCollection = collection(db, 'cashMovements');
        const movement: Omit<CashMovement, 'id'> = {
          type,
          amount: numAmount,
          date: getTimestamp(),
          notes: notes.trim(),
          userId: auth.currentUser?.uid || null,
        };

        await setDocWithCreatedBy(cashMovementsCollection, movement);
      };

      optimistFunction();
      onClose();
    } catch (err) {
      console.error(`Error processing ${type}:`, err);
      setError(
        err instanceof Error
          ? err.message
          : t('terminal.cashRegister.errorProcessing', `Error al procesar ${type === CashMovementType.ADDITION ? 'la adición' : 'el retiro'}`)
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(amount);
  };

  if (!isOpen) return null;

  const title = isAddition
    ? t('terminal.cashRegister.addCash', 'Agregar Efectivo')
    : t('terminal.cashRegister.withdrawCash', 'Retirar Efectivo');
  const buttonText = isAddition
    ? t('common.add', 'Agregar')
    : t('common.withdraw', 'Retirar');
  const icon = isAddition ? '⬆️' : '⬇️';

  return (
    <div className={styles.overlay}>
      <div className={styles.dialog}>
        <div className={`${styles.header} ${isAddition ? styles.headerAddition : styles.headerWithdrawal}`}>
          <div className={styles.headerIcon}>{icon}</div>
          <h2>{title}</h2>
        </div>

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>{t('common.loading', 'Cargando...')}</div>
          ) : !activeRegister ? (
            <div className={styles.message}>
              {error || t('terminal.cashRegister.noActiveRegister', 'No hay una caja abierta')}
            </div>
          ) : (
            <>
              {error && <div className={styles.error}>{error}</div>}

              <div className={styles.registerInfo}>
                <div className={styles.currentAmountCard}>
                  <div className={styles.currentAmountLabel}>
                    {t('terminal.cashRegister.currentAmount', 'Efectivo Actual en Caja')}
                  </div>
                  <div className={styles.currentAmountValue}>
                    {formatCurrency(activeRegister.currentAmount)}
                  </div>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="amount">
                  {t('common.amount', 'Monto')}:
                </label>
                <div className={styles.amountInputContainer}>
                  <span className={styles.currencySymbol}>$</span>
                  <input
                    id="amount"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className={styles.input}
                    min="0.01"
                    step="0.01"
                    autoFocus
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="notes">
                  {t('terminal.cashRegister.notes', 'Motivo')}:
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('terminal.cashRegister.notesPlaceholder', 'Motivo del movimiento')}
                  className={styles.textarea}
                  rows={3}
                />
              </div>

              {recentMovements.length > 0 && (
                <div className={styles.recentMovements}>
                  <h3 className={styles.recentMovementsTitle}>
                    {isAddition
                      ? t('terminal.cashRegister.recentAdditions', 'Adiciones Recientes')
                      : t('terminal.cashRegister.recentWithdrawals', 'Retiros Recientes')}
                  </h3>
                  <div className={styles.movementsList}>
                    {recentMovements.map((movement) => (
                      <div key={movement.id} className={styles.recentMovementItem}>
                        <div className={styles.movementAmount}>
                          {formatCurrency(movement.amount)}
                        </div>
                        <div className={styles.movementDetails}>
                          <div className={styles.movementNotes}>
                            {movement.notes || t('terminal.cashRegister.noNotes', 'Sin motivo')}
                          </div>
                        </div>
                        <div className={styles.movementTime}>
                          {formatDate(movement.date)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.actions}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel', 'Cancelar')}
          </button>
          {activeRegister && (
            <button
              className={`${styles.submitButton} ${isAddition ? styles.addButton : styles.withdrawButton}`}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading
                ? t('common.processing', 'Procesando...')
                : buttonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashMovementDialog;
